import { flagEnabled } from "./flags"
import Calc from "@/common/util/calculator"
import ColorUtil from "@/signomatic/util/colors"

const textrow = (
  zIndex: number,
  left: number,
  top: number,
  fill: string,
  text: string,
  fontFamily: string,
  fontSize: number,
  fontWeight: string,
  fontStyle: string,
  textDecoration: string,
  textAlign: "left" | "center" | "right"
): SignObjectV2 => {
  return {
    zIndex,
    left,
    top,
    fill,
    text,
    fontSize,
    fontWeight,
    fontFamily,
    fontStyle,
    lineHeight: 1,
    textDecoration,
    textAlign,
    type: "i-text",
    originX: "center",
    originY: "center",
    stroke: undefined,
    strokeWidth: 1,
    strokeDashArray: undefined,
    strokeLineCap: "butt",
    strokeLineJoin: "miter",
    strokeMiterLimit: 10,
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    flipX: false,
    flipY: false,
    opacity: 1,
    shadow: undefined,
    visible: true,
    clipTo: null,
    backgroundColor: "",
    fillRule: "nonzero",
    globalCompositeOperation: "source-over",
    path: null,
    textBackgroundColor: "",
    useNative: true,
    styles: {},
  }
}

const image = (
  zIndex: number,
  imageId: number,
  colored: boolean,
  fill: string,
  left: number,
  top: number,
  width: number,
  height: number,
  url: string
): SignObjectV2 => {
  return {
    type: "image",
    colored,
    zIndex,
    imageId,
    originX: "center",
    originY: "center",
    left,
    top,
    width,
    height,
    fill,
    src: url,
    imageName: "Symboler",
    stroke: undefined,
    strokeWidth: 1,
    strokeDashArray: undefined,
    strokeLineCap: "butt",
    strokeLineJoin: "miter",
    strokeMiterLimit: 10,
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    flipX: false,
    flipY: false,
    opacity: 1,
    shadow: undefined,
    visible: true,
    clipTo: null,
    backgroundColor: "",
    fillRule: "nonzero",
    globalCompositeOperation: "source-over",
    filters: [],
    crossOrigin: "",
    alignX: "none",
    alignY: "none",
    meetOrSlice: "meet",
    ratio: "1.0",
  }
}

export default {
  v1(code: SignStateV1, ratio: number, scale: number) {
    let index = 1
    const scaler = 0.45
    const pxmm = Calc.getDPI() / 25.4

    const width = code.width || code.fixed_width || 1
    const height = code.height || code.fixed_height || 1

    // Retain oval and square shape
    const fixShapes: StringMap<string> = {
      oval: "ellipse",
      none: "rect",
    }
    // Invalid shapes (nil, NilClass, triangle_top, "")
    // will be defaulted by SignValidator. All other shapes are allowed.
    const shape =
      code.v_sign_style && fixShapes[code.v_sign_style] !== undefined ? fixShapes[code.v_sign_style] : code.v_sign_style

    const state: SignStateV2 = {
      form: {
        width: width * pxmm,
        height: height * pxmm,
        color: code.v_background_color ? ColorUtil.normalize(code.v_background_color) : undefined,
        border: code.v_sign_border,
        "border-color": code.v_text_color ? ColorUtil.normalize(code.v_text_color) : undefined,
        fixMethod: code.v_hang_type,
        shape,
        "width-auto": false,
        "height-auto": false,
      },
      objects: [],
      canvas: {
        width: 0,
        height: 0,
      },
    }

    if (code.relpixels && code.relpixels > 0.0) {
      if (code.textrows && code.textrows.length > 0) {
        for (const row of code.textrows) {
          if (!row.left || !row.top || !row.width || !row.height || !row.size) {
            continue
          }

          const html = row.html || ""
          const div = document.createElement("div")
          div.innerHTML = html
          const text = div.textContent || div.innerText || ""

          const left = (row.left - (width - row.width - row.left)) * pxmm * scaler
          const top = (row.top - (height - row.height - row.top)) * pxmm * scaler
          const fill = code.v_text_color ? ColorUtil.normalize(code.v_text_color) : "#000000"
          const family = (row.family || "Arial").replace(/['"]/g, "")
          const size = row.size * pxmm
          const align = row.align || "left"
          const weight = html.toLowerCase().indexOf("<strong>") >= 0 ? "bold" : "normal"
          const style = html.toLowerCase().indexOf("<em>") >= 0 ? "italic" : "normal"
          const decoration = html.toLowerCase().indexOf("border-bottom") >= 0 ? "underline" : "normal"

          state.objects.push(textrow(index, left, top, fill, text, family, size, weight, style, decoration, align))
          index += 1
        }
      }
    } else {
      const textrows = []
      let family = ""
      let size = 0
      let align: "left" | "center" | "right" = "center"
      const vtext = code.v_text || ""

      const div = document.createElement("div")
      div.innerHTML = vtext

      for (const row of Array.from(div.childNodes)) {
        const el = row as any
        family = el.style.fontFamily.replace(/['"]/g, "")
        size = parseInt(el.style.fontSize, 10) * pxmm
        align = el.style.textAlign
        textrows.push(el.textContent || el.innerText || "")
      }

      const text = textrows.join("\n")
      const left = code.text_left ? code.text_left * pxmm * scaler : 0
      const top = code.text_top ? code.text_top * pxmm * scaler : 0
      const fill = code.v_text_color ? ColorUtil.normalize(code.v_text_color) : "#000000"
      const weight = vtext.toLowerCase().indexOf("<strong>") >= 0 ? "bold" : "normal"
      const style = vtext.toLowerCase().indexOf("<em>") >= 0 ? "italic" : "normal"
      const decoration = vtext.toLowerCase().indexOf("border-bottom") >= 0 ? "underline" : "normal"

      state.objects.push(textrow(index, left, top, fill, text, family, size, weight, style, decoration, align))
      index += 1
    }

    if (code.images && code.images.length > 0) {
      for (const img of code.images) {
        if (!img.name || !img.w || !img.h || !img.x || !img.y) {
          continue
        }

        const id = parseInt(img.name, 0)
        const colored = !img.bw
        const fill = img.color ? ColorUtil.normalize(img.color) : "#000000"
        const left = (img.x - (width - img.w - img.x)) * pxmm * scaler
        const top = (img.y - (height - img.h - img.y)) * pxmm * scaler
        const w = img.w * pxmm
        const h = img.h * pxmm

        const txtcolor = fill.substring(1, fill.length)
        let bgcolor = ColorUtil.normalize(code.v_background_color || "#ffffff")
        bgcolor = bgcolor.substring(1, bgcolor.length)

        const url =
          "https://" +
          window.location.host +
          "/image/get?id=" +
          id +
          "&width=" +
          w * scale * ratio +
          "&height=" +
          h * scale * ratio +
          "&color=" +
          colored +
          "&text_color=" +
          txtcolor +
          "&background_color=" +
          bgcolor

        state.objects.push(image(index, id, colored, fill, left, top, w, h, url))
        index += 1
      }
    }

    return state
  },
  v2(code: SignStateV2, defaultState: SignState, width: number, height: number): SignState {
    const customHolesFlag = flagEnabled("custom-holes-9377")
    const cornerRadiusFlag = flagEnabled("corner-radius-9390")
    const customHolesState = customHolesFlag
      ? {
          customHoleCount: code.form.customHoleCount,
          customHoleSizeMm: code.form.customHoleSizeMm,
          customHoleDistanceMm: code.form.customHoleDistanceMm,
          customHoleControlPattern: code.form.customHoleControlPattern,
        }
      : {}
    const cornerRadiusState = cornerRadiusFlag
      ? {
          cornerRadiusMm: code.form.cornerRadiusMm,
        }
      : {}

    const state: SignState = {
      ...defaultState,
      badgeName: code.form.badgeName,
      border: code.form.border || defaultState.border,
      borderColor: code.form["border-color"],
      color: code.form.color || defaultState.color,
      fixmethod: code.form.fixMethod,
      height: code.form.height ? Calc.pxToMm(code.form.height) : defaultState.height,
      width: code.form.width ? Calc.pxToMm(code.form.width) : defaultState.width,
      widthAuto: code.form["width-auto"] || false,
      heightAuto: code.form["height-auto"] || false,
      material: code.form.material || defaultState.material,
      shape: code.form.shape || defaultState.shape,
      thickness: code.form.thickness,
      doubleSided: code.form.doubleSided || false,
      side: code.form.side,
      preset: code.form.preset,
      sideState: code.form.sideState,
      pattern: code.form.pattern,
      metaType: code.form.metaType,
      metaProduct: code.form.metaProduct,
      ...customHolesState,
      ...cornerRadiusState,
    }

    if (["#fafafa", "#0000aa"].includes(state.color)) {
      state.material = "reflective"
    }

    let canvasWidthDiff = 0
    if (code.canvas && width !== code.canvas.width) {
      canvasWidthDiff = width - code.canvas.width
    }

    let canvasHeightDiff = 0
    if (code.canvas && height !== code.canvas.height) {
      canvasHeightDiff = height - code.canvas.height
    }

    const centerX = width / 2
    const centerY = height / 2

    if (code.objects && code.objects[0]) {
      state.contentColor = code.objects[0].fill

      for (const v2obj of code.objects) {
        let border: SignObjectBorder | undefined

        if (v2obj.objectBorderType && v2obj.objectBorderType.length > 0) {
          border = {
            shape: v2obj.objectBorderType,
            color: v2obj.objectBorderFill,
            borderColor: v2obj.objectBorderColor || "#000000",
            size: v2obj.objectBorderSize ? parseFloat(v2obj.objectBorderSize) : 1,
            thickness: v2obj.objectBorderThickness ? parseFloat(v2obj.objectBorderThickness) : 1,
          }
        }

        const left = v2obj.left ? v2obj.left + canvasWidthDiff / 2 - centerX : 0
        const top = v2obj.top ? v2obj.top + canvasHeightDiff / 2 - centerY : 0

        if (v2obj.type === "i-text") {
          const obj: SignTextState = {
            left,
            top,
            zindex: v2obj.zIndex ?? 0,
            oindex: v2obj.oIndex ?? v2obj.zIndex ?? 0,
            mirrored: v2obj.flipX || false,
            angle: v2obj.angle || 0,
            color: v2obj.fill || "#000000",
            text: v2obj.text || "",
            size:
              v2obj.fontSize && v2obj.scaleX
                ? parseFloat(Calc.pxToFontsize(v2obj.fontSize * v2obj.scaleX).toFixed(1))
                : 50,
            font: v2obj.fontFamily?.replace(/['"]/g, "") || "Arial",
            bold: v2obj.fontWeight === "bold",
            italic: v2obj.fontStyle === "italic",
            underlined: v2obj.textDecoration === "underline",
            justify: v2obj.textAlign || "left",
            side: v2obj.side,
            identity: Symbol(),
            width: v2obj.width || 0,
            border,
          }

          state.objects.push(obj)
        } else if (v2obj.type === "image") {
          const objWidth = (v2obj.width || 0) * (v2obj.scaleX || 1) || 10
          const objHeight = (v2obj.height || 0) * (v2obj.scaleY || 1) || 10

          const obj: SignImageState = {
            left,
            top,
            zindex: v2obj.zIndex ?? 0,
            oindex: v2obj.oIndex ?? v2obj.zIndex ?? 0,
            mirrored: v2obj.flipX || false,
            angle: v2obj.angle || 0,
            color: v2obj.fill || "#000000",
            width: objWidth,
            height: objHeight,
            ratio: objHeight / objWidth,
            id: parseInt(v2obj.imageId, 0),
            colored: v2obj.colored || false,
            name: v2obj.imageName || "image",
            src: v2obj.src,
            side: v2obj.side,
            identity: Symbol(),
            border,
          }

          if (!obj.colored && v2obj.src && v2obj.src.includes("color=true")) {
            obj.colored = true
          }

          state.objects.push(obj)
        }
      }
    }

    return state
  },
  v2form(state: SignState): SignStateV2Form {
    const customHolesFlag = flagEnabled("custom-holes-9377")
    const cornerRadiusFlag = flagEnabled("corner-radius-9390")
    const customHolesState = customHolesFlag
      ? {
          customHoleCount: state.customHoleCount,
          customHoleSizeMm: state.customHoleSizeMm,
          customHoleDistanceMm: state.customHoleDistanceMm,
          customHoleControlPattern: state.customHoleControlPattern,
        }
      : {}
    const cornerRadiusState = cornerRadiusFlag
      ? {
          cornerRadiusMm: state.cornerRadiusMm,
        }
      : {}

    return {
      badgeName: state.badgeName,
      border: state.border,
      "border-color": state.borderColor,
      color: state.color,
      material: state.material,
      shape: state.shape,
      thickness: state.thickness,
      fixMethod: state.fixmethod,
      height: state.heightPx,
      width: state.widthPx,
      "width-auto": state.widthAuto,
      "height-auto": state.heightAuto,
      doubleSided: state.doubleSided,
      side: state.side,
      preset: state.preset,
      sideState: state.sideState,
      pattern: state.pattern,
      metaType: state.metaType,
      metaProduct: state.metaProduct,
      ...customHolesState,
      ...cornerRadiusState,
    }
  },
  isV1(code: any): code is SignStateV1 {
    return typeof code.v_background_color === "string" && typeof code.form === "undefined"
  },
  isV2(code: any): code is SignStateV2 {
    return typeof code.version === "undefined" && typeof code.form === "object"
  },
  isV3(code: any): code is SignState {
    return typeof code.version === "number" && typeof code.form === "undefined"
  },
}
